import axios from 'axios';
import {push} from 'connected-react-router';
import {AsyncResult, fromResult, Loaded, ServerError} from 'Core/models/Async';
import * as E from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import {all, call, put, takeLatest} from 'redux-saga/effects';
import {handleResponse} from 'Shared/utilities/fetchHelpers';
import {NonEmptyString} from 'Core/models/EmptyString';
import {getApiCallGenerator} from 'Shared/utilities/sagaApiGenerators';
import userApi from 'Users/api/userApi';
import {
  changePasswordResetRequestQuestions,
  changePasswordUpdate,
  changeSecurityQuestionsForPasswordRecovery,
  getPasswordResetRequestQuestions,
  PasswordRecoveryActionType,
  requestPasswordReset,
  requestQuesForPasswordReset,
  updatePassword,
  validAns,
  validUsername,
  InvalidUsernameMessage,
  ErrorUsernameMessage,
} from 'Users/ducks/passwordRecovery';
import {PasswordResetForm, PasswordUpdate} from 'Users/models/PasswordResetForm';
import {SecurityQuestionByPasswordPayload} from 'Users/models/SecurityQuestionByPasswordPayload';
import {UserRouteConstants} from 'Users/Routes';

function* requestPasswordResetApi(action: ReturnType<typeof requestPasswordReset>) {
  const requestPasswordResetApiCall = () => {
    const ADDRESS_ROOT_URL: string = `api/user/v1/portal/${action.payload.username.value}/password/forgotPassword/ALLBA`;
    return axios.put(`${ADDRESS_ROOT_URL}`, action.payload.obj);
  };

  const resp: AsyncResult<SecurityQuestionByPasswordPayload> = yield call(requestPasswordResetApiCall);

  yield put(validAns(resp['data']['attemptsRemainingNbr']));

  if (resp['status'] == 200) yield put(push(UserRouteConstants.PASSWORD_RECOVERY_EMAIL_SENT));
  else yield put(push(UserRouteConstants.PASSWORD_RECOVERY_SHOW_SECURITY_QUES));
}

export function* fetchQuesApi(action: ReturnType<typeof requestQuesForPasswordReset>) {
  const requestPasswordResetApiCall = (username: string) => {
    const ADDRESS_ROOT_URL: string = `api/user/v1/portal/${username}/validateUserIdentity/ALLBA`;
    return axios
      .request({
        method: 'get',
        url: `${ADDRESS_ROOT_URL}`,
      })
      .then(handleResponse);
  };

  const resp: AsyncResult<SecurityQuestionByPasswordPayload> = yield call(
    requestPasswordResetApiCall,
    action.payload.username.value.trim()
  );

  yield pipe(
    resp,
    E.map(fromResult),
    E.fold(
      error => updateValue(UserRouteConstants.PASSWORD_RECOVERY, null, error.errorDetails),
      value => updateValue(UserRouteConstants.PASSWORD_RECOVERY_SHOW_SECURITY_QUES, value)
    )
  );
}

function* updateValue(nextStage: any, value: any = null, error: any = null) {
  if (error) {
    if (error == 'Invalid Username.') {
      yield put(validUsername(NonEmptyString.create(InvalidUsernameMessage)));
    } else {
      yield put(validUsername(NonEmptyString.create(ErrorUsernameMessage)));
    }
  } else {
    if (value) {
      yield put(changeSecurityQuestionsForPasswordRecovery(new Loaded(value.Data)));
    }
  }
  yield put(push(nextStage));
}

export function* fetchQuesSaga() {
  yield takeLatest(PasswordRecoveryActionType.REQUEST_PASSWORD_RESET_QUES, fetchQuesApi);
}

export function* resetPasswordSaga() {
  yield takeLatest(PasswordRecoveryActionType.REQUEST_PASSWORD_RESET, requestPasswordResetApi);
}

export function* getPasswordResetRequestQuestionsFromApi(action: ReturnType<typeof getPasswordResetRequestQuestions>) {
  yield getApiCallGenerator(
    () => userApi.getPasswordResetRequest(action.payload.requestKey),
    changePasswordResetRequestQuestions
  )({});
}

export function* getPasswordResetRequestQuestionsSaga() {
  yield takeLatest(
    PasswordRecoveryActionType.GET_PASSWORD_RESET_REQUEST_QUESTIONS,
    getPasswordResetRequestQuestionsFromApi
  );
}

export function* updatePasswordApi(action: ReturnType<typeof updatePassword>) {
  yield put(changePasswordUpdate(PasswordUpdate.Saving({})));
  const result: AsyncResult<unknown> = yield userApi.updatePassword(
    action.payload.id,
    action.payload.requestKey,
    action.payload.independentAgentProfile,
    action.payload.passwordResetForm
  );

  if (E.isRight(result)) {
    yield put(changePasswordUpdate(PasswordUpdate.Complete({})));
  } else {
    yield put(
      changePasswordUpdate(
        PasswordUpdate.Entry({
          form: PasswordResetForm.FailureToUpdate({
            ...action.payload.passwordResetForm,
            errorMessage: result.left instanceof ServerError ? result.left.errorDetails : undefined,
          }),
        })
      )
    );
  }
}

export function* updatePasswordSaga() {
  yield takeLatest(PasswordRecoveryActionType.UPDATE_PASSWORD, updatePasswordApi);
}

export default function*() {
  yield all([resetPasswordSaga(), getPasswordResetRequestQuestionsSaga(), updatePasswordSaga(), fetchQuesSaga()]);
}
